// src/pages/Projects.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, RefreshCw, FolderOpen, Trash2, X, ChevronLeft, ChevronRight,
  Filter, ChevronDown, Clock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { projectsAPI, adminAPI, aoAPI, resourcesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './TimesheetDetail.css';

// ── Constantes ────────────────────────────────────────────────────────────────

const PER_PAGE_OPTIONS = [25, 50, 100];

const STATUT_META = {
  EN_COURS:   { label: 'Actif',      color: '#16A34A', bg: 'rgba(34,197,94,0.10)' },
  ARCHIVE:    { label: 'Archivé',    color: '#8B8FA8', bg: 'rgba(139,143,168,0.12)' },
  EN_ATTENTE: { label: 'En attente', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
};

const DEVISES = [
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'CHF', label: 'CHF — Franc suisse' },
  { value: 'DZD', label: 'DZD — Dinar algérien' },
  { value: 'TND', label: 'TND — Dinar tunisien' },
  { value: 'MAD', label: 'MAD — Dirham marocain' },
  { value: 'USD', label: 'USD — Dollar américain' },
  { value: 'GBP', label: 'GBP — Livre sterling' },
];

const TYPE_OPTIONS = [
  { value: '',          label: 'Tous' },
  { value: 'REGIE',     label: 'Régie' },
  { value: 'FORFAIT',   label: 'Forfait' },
  { value: 'INTERNE',   label: 'Interne' },
  { value: 'PILOTAGE',  label: 'Pilotage' },
];

const EMPTY_FORM = {
  nom: '', statut: 'EN_COURS', type_projet: '', date_debut: '', date_fin: '',
  responsable_id: '', besoin_id: '', agence: '', client: '', client_final: '',
  reference_interne: '', devise: 'EUR', adresse: '', code_postal: '', pays: '',
  commentaire: '', prestations: [],
};

const EMPTY_FILTERS = {
  nom: '', ref: '', profil: '', client: '', type: '',
  date_from: '', date_to: '', statut: '', charge_affaires: '', client_final: '',
};

const EMPTY_COL = { ref: '', profil: '', client: '', client_final: '', charge: '' };

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR');
}

function StatutBadge({ statut }) {
  const m = STATUT_META[statut];
  if (!m) return <span style={{ color: 'var(--text-faint)' }}>—</span>;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '3px 10px',
      borderRadius: 12, fontSize: '0.75rem', fontWeight: 700,
      color: m.color, background: m.bg, border: `1px solid ${m.color}33`,
      whiteSpace: 'nowrap',
    }}>{m.label}</span>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────

export default function Projects() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const canEdit   = ['SUPER_ADMIN', 'ADMIN', 'RH', 'BUSINESS_MANAGER'].includes(user?.role);
  const isCollab  = (user?.role || '').toLowerCase() === 'collaborateur';

  const [items,     setItems]    = useState([]);
  const [loading,   setLoading]  = useState(true);
  const [activeTab, setActiveTab] = useState('actifs');
  const [perPage,   setPerPage]  = useState(25);
  const [page,      setPage]     = useState(1);
  const [selected,  setSelected] = useState(new Set());

  // Panneau filtre : pending vs appliqué
  const [filters,  setFilters]  = useState(EMPTY_FILTERS);
  const [applied,  setApplied]  = useState(EMPTY_FILTERS);
  const [colF,     setColF]     = useState(EMPTY_COL);

  // Modal
  const [pendingProjects, setPendingProjects] = useState([]);

  const [modal,     setModal]     = useState(null);
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [saving,    setSaving]    = useState(false);
  const [users,     setUsers]     = useState([]);
  const [resources, setResources] = useState([]);
  const [aos,       setAos]       = useState([]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (isCollab) params.for_me = true;
      const [res, pendingRes] = await Promise.all([
        projectsAPI.list(params),
        projectsAPI.list({ statut: 'EN_ATTENTE', limit: 100 }),
      ]);
      setItems(res.data || []);
      setPendingProjects(pendingRes.data || []);
      setPage(1);
      setSelected(new Set());
    } catch { toast.error('Erreur chargement projets'); }
    finally { setLoading(false); }
  }, [isCollab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Filtrage client-side ───────────────────────────────────────────────────
  const displayed = useMemo(() => {
    let list = [...items];

    // Tab
    if (activeTab === 'actifs') list = list.filter(p => p.statut === 'EN_COURS');
    if (activeTab === 'fermes') list = list.filter(p => p.statut === 'ARCHIVE');

    // Panel appliqué
    const af = applied;
    const ci = (s) => (s || '').toLowerCase();
    if (af.nom)             list = list.filter(p => ci(p.nom).includes(ci(af.nom)));
    if (af.ref)             list = list.filter(p => ci(p.reference_interne).includes(ci(af.ref)));
    if (af.profil)          list = list.filter(p => p.prestations?.some(pr =>
      ci(`${pr.resource_prenom||''} ${pr.resource_nom||''}`).includes(ci(af.profil))));
    if (af.client)          list = list.filter(p => ci(p.client).includes(ci(af.client)));
    if (af.type)            list = list.filter(p => p.type_projet === af.type);
    if (af.statut)          list = list.filter(p => p.statut === af.statut);
    if (af.charge_affaires) list = list.filter(p => ci(`${p.responsable_prenom||''} ${p.responsable_nom||''}`).includes(ci(af.charge_affaires)));
    if (af.client_final)    list = list.filter(p => ci(p.client_final).includes(ci(af.client_final)));
    if (af.date_from)       list = list.filter(p => { const d = p.boond_created_at || p.created_at; return d && new Date(d) >= new Date(af.date_from); });
    if (af.date_to)         list = list.filter(p => { const d = p.boond_created_at || p.created_at; return d && new Date(d) <= new Date(af.date_to); });

    // Filtres colonnes inline
    if (colF.ref)          list = list.filter(p => ci(p.reference_interne).includes(ci(colF.ref)));
    if (colF.profil)       list = list.filter(p => p.prestations?.some(pr => ci(`${pr.resource_prenom||''} ${pr.resource_nom||''}`).includes(ci(colF.profil))));
    if (colF.client)       list = list.filter(p => ci(p.client).includes(ci(colF.client)));
    if (colF.client_final) list = list.filter(p => ci(p.client_final).includes(ci(colF.client_final)));
    if (colF.charge)       list = list.filter(p => ci(`${p.responsable_prenom||''} ${p.responsable_nom||''}`).includes(ci(colF.charge)));

    return list;
  }, [items, activeTab, applied, colF]);

  const totalPages = Math.ceil(displayed.length / perPage);
  const pageItems  = displayed.slice((page - 1) * perPage, page * perPage);

  // ── Sélection ──────────────────────────────────────────────────────────────
  const allOnPage = pageItems.length > 0 && pageItems.every(p => selected.has(p.id));
  const toggleAll = () => {
    const next = new Set(selected);
    allOnPage ? pageItems.forEach(p => next.delete(p.id)) : pageItems.forEach(p => next.add(p.id));
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  // ── Modal ──────────────────────────────────────────────────────────────────
  const loadModalData = async () => {
    try {
      const [ur, rr, ar] = await Promise.all([adminAPI.listUsers(), resourcesAPI.list({ limit: 500 }), aoAPI.getNew()]);
      setUsers(ur.data || []);
      setResources(rr.data || []);
      setAos(ar.data || []);
    } catch {}
  };

  const openCreate = () => { setForm(EMPTY_FORM); setModal({ mode: 'create' }); loadModalData(); };
  const openEdit   = (e, project) => {
    e.stopPropagation();
    setForm({
      nom:               project.nom || '',
      statut:            project.statut || 'EN_COURS',
      type_projet:       project.type_projet || '',
      date_debut:        project.date_debut || '',
      date_fin:          project.date_fin || '',
      responsable_id:    project.responsable_id || '',
      besoin_id:         project.besoin_id || '',
      agence:            project.agence || '',
      client:            project.client || '',
      client_final:      project.client_final || '',
      reference_interne: project.reference_interne || '',
      devise:            project.devise || 'EUR',
      adresse:           project.adresse || '',
      code_postal:       project.code_postal || '',
      pays:              project.pays || '',
      commentaire:       project.commentaire || '',
      prestations:       (project.prestations || []).map(p => ({ resource_id: p.resource_id || '', poste: p.poste || '' })),
    });
    setModal({ mode: 'edit', id: project.id });
    loadModalData();
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer ce projet ?')) return;
    try { await projectsAPI.remove(id); toast.success('Projet supprimé'); fetchData(); }
    catch { toast.error('Erreur suppression'); }
  };

  const handleSave = async () => {
    if (!form.nom.trim()) { toast.error('Le nom est obligatoire'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        responsable_id: form.responsable_id || null,
        besoin_id:      form.besoin_id      || null,
        date_debut:     form.date_debut     || null,
        date_fin:       form.date_fin       || null,
        prestations:    (form.prestations || []).filter(p => p.resource_id)
          .map(p => ({ resource_id: p.resource_id, poste: p.poste || null })),
      };
      if (modal.mode === 'create') {
        const r = await projectsAPI.create(payload);
        toast.success('Projet créé');
        navigate('/projects/' + r.data.id);
      } else {
        await projectsAPI.update(modal.id, payload);
        toast.success('Projet mis à jour');
        setModal(null);
        fetchData();
      }
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const sf = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  const hasApplied = Object.values(applied).some(Boolean);

  const TABS = [
    { key: 'actifs', label: 'Actifs' },
    { key: 'fermes', label: 'Fermés' },
    { key: 'tous',   label: 'Tous les projets' },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="page-header" style={{ marginBottom: 0, paddingBottom: 0 }}>
        <div className="page-header-left" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <FolderOpen size={22} style={{ color: 'var(--orange-500)' }}/>
          <h1 style={{ margin: 0 }}>Projets</h1>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={fetchData}><RefreshCw size={15}/></button>
          {canEdit && (
            <button className="btn btn-primary" onClick={openCreate}>
              <Plus size={15}/> Nouveau projet
            </button>
          )}
          {selected.size > 0 && (
            <button className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Sélectionnés ({selected.size}) <ChevronDown size={13}/>
            </button>
          )}
        </div>
      </div>

      {/* ── Bannière projets en attente ────────────────────────────── */}
      {pendingProjects.length > 0 && (
        <div style={{
          margin: '12px 0 0',
          padding: '10px 16px',
          background: 'rgba(245,158,11,0.1)',
          border: '1px solid rgba(245,158,11,0.35)',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}>
          <Clock size={16} style={{ color: '#F59E0B', flexShrink: 0 }} />
          <span style={{ fontWeight: 700, fontSize: '0.875rem', color: '#F59E0B' }}>
            {pendingProjects.length} projet{pendingProjects.length > 1 ? 's' : ''} en attente de création
          </span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {pendingProjects.map(p => (
              <button key={p.id}
                onClick={e => openEdit(e, p)}
                style={{
                  background: 'rgba(245,158,11,0.15)',
                  border: '1px solid rgba(245,158,11,0.4)',
                  borderRadius: 6,
                  padding: '3px 10px',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: '#F59E0B',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}>
                {p.nom || '(sans nom)'}{p.client ? ` — ${p.client}` : ''}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Tabs ───────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
        {TABS.map(t => (
          <button key={t.key}
            onClick={() => { setActiveTab(t.key); setPage(1); }}
            style={{
              background: 'none', border: 'none', padding: '10px 20px',
              fontSize: '0.875rem', fontWeight: activeTab === t.key ? 700 : 500,
              color: activeTab === t.key ? 'var(--orange-500)' : 'var(--text-muted)',
              borderBottom: activeTab === t.key ? '2px solid var(--orange-500)' : '2px solid transparent',
              marginBottom: -1, cursor: 'pointer', transition: 'color 0.15s',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Panneau filtres ────────────────────────────────────────── */}
      <div style={{
        background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)',
        padding: '16px 20px 14px',
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px 14px' }}>
          <div>
            <label style={LBL}>Nom du projet</label>
            <div style={{ position: 'relative' }}>
              <Filter size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-faint)', pointerEvents: 'none' }}/>
              <input style={{ ...INP, paddingLeft: 26 }} placeholder="Rechercher…"
                value={filters.nom} onChange={e => setFilters(f => ({ ...f, nom: e.target.value }))}/>
            </div>
          </div>
          <div>
            <label style={LBL}>Réf.</label>
            <div style={{ position: 'relative' }}>
              <Filter size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-faint)', pointerEvents: 'none' }}/>
              <input style={{ ...INP, paddingLeft: 26 }} placeholder="Référence…"
                value={filters.ref} onChange={e => setFilters(f => ({ ...f, ref: e.target.value }))}/>
            </div>
          </div>
          <div>
            <label style={LBL}>Profil</label>
            <input style={INP} placeholder="Nom du profil…"
              value={filters.profil} onChange={e => setFilters(f => ({ ...f, profil: e.target.value }))}/>
          </div>
          <div>
            <label style={LBL}>Client facturé</label>
            <input style={INP} placeholder="Client…"
              value={filters.client} onChange={e => setFilters(f => ({ ...f, client: e.target.value }))}/>
          </div>
          <div>
            <label style={LBL}>Type</label>
            <select style={INP} value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}>
              {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div>
            <label style={LBL}>Date de création — du</label>
            <input type="date" style={INP} value={filters.date_from}
              onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}/>
          </div>
          <div>
            <label style={LBL}>Au</label>
            <input type="date" style={INP} value={filters.date_to}
              onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}/>
          </div>
          <div>
            <label style={LBL}>Statut</label>
            <select style={INP} value={filters.statut} onChange={e => setFilters(f => ({ ...f, statut: e.target.value }))}>
              <option value="">Tous</option>
              <option value="EN_COURS">Actif</option>
              <option value="ARCHIVE">Archivé</option>
            </select>
          </div>
          <div>
            <label style={LBL}>Chargé d'affaire</label>
            <input style={INP} placeholder="Nom…"
              value={filters.charge_affaires} onChange={e => setFilters(f => ({ ...f, charge_affaires: e.target.value }))}/>
          </div>
          <div>
            <label style={LBL}>Client final</label>
            <input style={INP} placeholder="Client final…"
              value={filters.client_final} onChange={e => setFilters(f => ({ ...f, client_final: e.target.value }))}/>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-faint)', fontStyle: 'italic' }}>
            ET / OU, opérateurs typés
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            {hasApplied && (
              <button className="btn btn-ghost btn-sm"
                onClick={() => { setFilters(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); setPage(1); }}>
                <X size={13}/> Effacer
              </button>
            )}
            <button className="btn btn-primary" style={{ padding: '6px 20px' }}
              onClick={() => { setApplied({ ...filters }); setPage(1); }}>
              Rechercher
            </button>
          </div>
        </div>
      </div>

      {/* ── Barre sélection + pagination ──────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 0', fontSize: '0.8125rem', color: 'var(--text-muted)',
      }}>
        <span style={{ fontWeight: 600 }}>{selected.size} Sélectionné(e)</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Afficher
            <select style={{
              border: '1px solid var(--border)', borderRadius: 5, padding: '2px 6px',
              background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.8125rem',
            }}
              value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}>
              {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            entrées
          </label>
          {!loading && (
            <span>
              Affichage de{' '}
              {displayed.length > 0 ? Math.min((page - 1) * perPage + 1, displayed.length) : 0} à{' '}
              {Math.min(page * perPage, displayed.length)} sur {displayed.length} entrée{displayed.length !== 1 ? 's' : ''}
            </span>
          )}
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setPage(1)} disabled={page === 1}>«</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft size={13}/>
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const pn = start + i;
                return pn <= totalPages ? (
                  <button key={pn} className="btn btn-ghost btn-sm" onClick={() => setPage(pn)}
                    style={{ fontWeight: pn === page ? 700 : 400, color: pn === page ? 'var(--orange-500)' : undefined, minWidth: 28 }}>
                    {pn}
                  </button>
                ) : null;
              })}
              <button className="btn btn-ghost btn-sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                <ChevronRight size={13}/>
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
            </div>
          )}
        </div>
      </div>

      {/* ── Table ─────────────────────────────────────────────────── */}
      {loading ? (
        <div className="loading-state"><div className="spinner"/></div>
      ) : displayed.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 0' }}>
          <FolderOpen size={36} style={{ opacity: 0.3, display: 'block', margin: '0 auto 12px' }}/>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Aucun projet trouvé</div>
          {canEdit && (
            <button className="btn btn-primary" onClick={openCreate}><Plus size={15}/> Créer</button>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 34, paddingRight: 0 }}>
                    <input type="checkbox" checked={allOnPage} onChange={toggleAll} style={{ cursor: 'pointer' }}/>
                  </th>
                  <th>PROJET</th>
                  <th>RÉF.</th>
                  <th>TYPE</th>
                  <th>PROFILS AFFECTÉS</th>
                  <th>CLIENT FACTURÉ</th>
                  <th>CLIENT FINAL</th>
                  <th>CHARGÉS D'AFFAIRES</th>
                  <th style={{ textAlign: 'center' }}>STOCK EN COURS</th>
                  <th style={{ textAlign: 'center' }}>STOCK</th>
                  <th>DATE DE CRÉATION</th>
                  <th>STATUT</th>
                  {canEdit && <th style={{ width: 36 }}/>}
                </tr>
                {/* Ligne de filtre inline par colonne */}
                <tr style={{ background: 'var(--bg-elevated)' }}>
                  <td/>
                  <td/>
                  <td><input style={COLINP} placeholder="Rechercher…" value={colF.ref}          onChange={e => setColF(f => ({ ...f, ref: e.target.value }))}/></td>
                  <td/>
                  <td><input style={COLINP} placeholder="Rechercher…" value={colF.profil}       onChange={e => setColF(f => ({ ...f, profil: e.target.value }))}/></td>
                  <td><input style={COLINP} placeholder="Rechercher…" value={colF.client}       onChange={e => setColF(f => ({ ...f, client: e.target.value }))}/></td>
                  <td><input style={COLINP} placeholder="Rechercher…" value={colF.client_final} onChange={e => setColF(f => ({ ...f, client_final: e.target.value }))}/></td>
                  <td><input style={COLINP} placeholder="Rechercher…" value={colF.charge}       onChange={e => setColF(f => ({ ...f, charge: e.target.value }))}/></td>
                  <td/><td/><td/><td/>
                  {canEdit && <td/>}
                </tr>
              </thead>
              <tbody>
                {pageItems.map(p => {
                  const createdAt  = p.boond_created_at || p.created_at;
                  const responsable = [p.responsable_prenom, p.responsable_nom].filter(Boolean).join(' ');
                  return (
                    <tr key={p.id} onClick={() => navigate('/projects/' + p.id)} style={{ cursor: 'pointer' }}>
                      <td style={{ paddingRight: 0 }} onClick={e => { e.stopPropagation(); toggleOne(p.id); }}>
                        <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleOne(p.id)} style={{ cursor: 'pointer' }}/>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--orange-500)' }}>{p.nom}</div>
                        {p.agence && <div style={{ fontSize: '0.72rem', color: 'var(--text-faint)', marginTop: 1 }}>{p.agence}</div>}
                      </td>
                      <td style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {p.reference_interne || <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}>—</span>}
                      </td>
                      <td>
                        {p.type_projet ? (
                          <span style={{
                            fontSize: '0.7rem', padding: '2px 8px', borderRadius: 6,
                            background: 'rgba(139,143,168,0.12)', color: 'var(--text-muted)',
                            border: '1px solid var(--border)', fontWeight: 600,
                          }}>{TYPE_OPTIONS.find(t => t.value === p.type_projet)?.label || p.type_projet}</span>
                        ) : <span style={{ color: 'var(--text-faint)' }}>—</span>}
                      </td>
                      <td>
                        {p.prestations?.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {p.prestations.slice(0, 3).map(pr => (
                              <span key={pr.id} style={{ fontSize: '0.75rem', color: '#60A5FA' }}>
                                {pr.resource_prenom} {pr.resource_nom}
                              </span>
                            ))}
                            {p.prestations.length > 3 && (
                              <span style={{ fontSize: '0.7rem', color: 'var(--text-faint)' }}>+{p.prestations.length - 3}</span>
                            )}
                          </div>
                        ) : <span style={{ color: 'var(--text-faint)', fontSize: '0.8rem' }}>—</span>}
                      </td>
                      <td style={{ fontSize: '0.8125rem' }}>{p.client || <span style={{ color: 'var(--text-faint)' }}>—</span>}</td>
                      <td style={{ fontSize: '0.8125rem' }}>{p.client_final || <span style={{ color: 'var(--text-faint)' }}>—</span>}</td>
                      <td style={{ fontSize: '0.8125rem', color: '#60A5FA' }}>
                        {responsable || <span style={{ color: 'var(--text-faint)' }}>—</span>}
                      </td>
                      <td style={{ textAlign: 'center', fontSize: '0.8125rem', color: 'var(--text-faint)' }}>0</td>
                      <td style={{ textAlign: 'center', fontSize: '0.8125rem', color: 'var(--text-faint)' }}>0</td>
                      <td style={{ fontSize: '0.8125rem', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                        {fmtDate(createdAt)}
                      </td>
                      <td><StatutBadge statut={p.statut}/></td>
                      {canEdit && (
                        <td onClick={e => e.stopPropagation()}>
                          <button className="btn btn-ghost btn-sm" onClick={e => handleDelete(e, p.id)}
                            style={{ color: 'var(--danger-600)', padding: '4px 6px' }}>
                            <Trash2 size={13}/>
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Modal création / édition ───────────────────────────────── */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" style={{ width: 660, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modal.mode === 'create' ? 'Nouveau projet' : 'Modifier le projet'}</h3>
              <button className="modal-close" onClick={() => setModal(null)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">

                {/* Nom */}
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Nom du projet *</label>
                  <input className="form-control" value={form.nom} onChange={sf('nom')} placeholder="Ex: Projet transformation digitale…"/>
                </div>

                {/* Réf. interne + Type */}
                <div className="form-group">
                  <label className="form-label">Référence interne</label>
                  <input className="form-control" value={form.reference_interne} onChange={sf('reference_interne')} placeholder="Ex: PU-41288-087663"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Type de projet</label>
                  <select className="form-control" value={form.type_projet} onChange={sf('type_projet')}>
                    {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>

                {/* Statut + Devise */}
                <div className="form-group">
                  <label className="form-label">Statut</label>
                  <select className="form-control" value={form.statut} onChange={sf('statut')}>
                    <option value="EN_COURS">Actif</option>
                    <option value="ARCHIVE">Archivé</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Devise</label>
                  <select className="form-control" value={form.devise} onChange={sf('devise')}>
                    {DEVISES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>

                {/* Client facturé + Client final */}
                <div className="form-group">
                  <label className="form-label">Client facturé</label>
                  <input className="form-control" value={form.client} onChange={sf('client')} placeholder="Nom du client"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Client final</label>
                  <input className="form-control" value={form.client_final} onChange={sf('client_final')} placeholder="Client final (utilisateur)"/>
                </div>

                {/* Chargé d'affaire */}
                <div className="form-group">
                  <label className="form-label">Chargé d'affaire / Responsable</label>
                  <select className="form-control" value={form.responsable_id} onChange={sf('responsable_id')}>
                    <option value="">— Sélectionner —</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>)}
                  </select>
                </div>

                {/* AO associé */}
                <div className="form-group">
                  <label className="form-label">AO / Besoin associé</label>
                  <select className="form-control" value={form.besoin_id} onChange={sf('besoin_id')}>
                    <option value="">— Aucun —</option>
                    {aos.map(ao => (
                      <option key={ao.id} value={ao.id}>
                        {ao.job_title || ao.ao_code || String(ao.id).slice(0, 8)}{ao.client ? ` — ${ao.client}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Dates */}
                <div className="form-group">
                  <label className="form-label">Date de début</label>
                  <input type="date" className="form-control" value={form.date_debut} onChange={sf('date_debut')}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Date de fin</label>
                  <input type="date" className="form-control" value={form.date_fin} onChange={sf('date_fin')}/>
                </div>

                {/* Agence + Pays + CP */}
                <div className="form-group">
                  <label className="form-label">Agence / Site</label>
                  <select className="form-control" value={form.agence} onChange={sf('agence')}>
                    <option value="">— Sélectionner —</option>
                    <option value="FRANCE">France</option>
                    <option value="BELGIQUE">Belgique</option>
                    <option value="ALGERIE">Algérie</option>
                    <option value="TUNISIE">Tunisie</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Pays</label>
                  <input className="form-control" value={form.pays} onChange={sf('pays')} placeholder="Ex: France"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Code postal</label>
                  <input className="form-control" value={form.code_postal} onChange={sf('code_postal')} placeholder="75001"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Adresse</label>
                  <input className="form-control" value={form.adresse} onChange={sf('adresse')} placeholder="Adresse du projet"/>
                </div>

                {/* Profils affectés */}
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <label className="form-label" style={{ margin: 0 }}>Profils affectés</label>
                    <button type="button" className="btn btn-ghost btn-sm" style={{ gap: 4 }}
                      onClick={() => setForm(f => ({ ...f, prestations: [...(f.prestations || []), { resource_id: '', poste: '' }] }))}>
                      <Plus size={13}/> Ajouter
                    </button>
                  </div>
                  {(form.prestations || []).length === 0 ? (
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-faint)', padding: '8px 0' }}>Aucun profil — cliquez "Ajouter"</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {(form.prestations || []).map((pp, i) => (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'center' }}>
                          <select className="form-control" value={pp.resource_id}
                            onChange={e => setForm(f => { const arr = [...f.prestations]; arr[i] = { ...arr[i], resource_id: e.target.value }; return { ...f, prestations: arr }; })}>
                            <option value="">— Ressource —</option>
                            {resources.map(r => (
                              <option key={r.id} value={r.id}>
                                {r.full_name || [r.first_name, r.last_name].filter(Boolean).join(' ') || r.email || String(r.id).slice(0, 8)}
                              </option>
                            ))}
                          </select>
                          <input className="form-control" placeholder="Poste / rôle (ex: Dev Senior)" value={pp.poste}
                            onChange={e => setForm(f => { const arr = [...f.prestations]; arr[i] = { ...arr[i], poste: e.target.value }; return { ...f, prestations: arr }; })}/>
                          <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--danger-600)' }}
                            onClick={() => setForm(f => ({ ...f, prestations: f.prestations.filter((_, j) => j !== i) }))}>
                            <Trash2 size={13}/>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Commentaire */}
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Commentaire</label>
                  <textarea className="form-control" rows={3} value={form.commentaire} onChange={sf('commentaire')}
                    placeholder="Notes, contexte, besoin…" style={{ resize: 'vertical' }}/>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.nom.trim()}>
                {saving ? 'Sauvegarde…' : modal.mode === 'create' ? 'Créer le projet' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles utilitaires ────────────────────────────────────────────────────────

const LBL = {
  display: 'block', fontSize: '0.75rem', fontWeight: 600,
  color: 'var(--text-muted)', marginBottom: 4,
};

const INP = {
  width: '100%', padding: '7px 10px', border: '1px solid var(--border)',
  borderRadius: 6, fontSize: '0.8125rem', boxSizing: 'border-box',
  background: 'var(--bg-card)', color: 'var(--text-primary)',
  outline: 'none', fontFamily: 'inherit',
};

const COLINP = {
  width: '100%', padding: '4px 8px', border: '1px solid var(--border)',
  borderRadius: 5, fontSize: '0.75rem', boxSizing: 'border-box',
  background: 'var(--bg-card)', color: 'var(--text-primary)',
  outline: 'none', fontFamily: 'inherit',
};
